import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Truck, Plane, CheckCircle2, AlertCircle, 
  Loader2, ArrowRight, Package, Calendar, MapPin, 
  ShieldCheck, Mail, BellRing, Phone, Building2, User,
  ThermometerSnowflake, Clock, RefreshCw, Copy, Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CustomerRequestBooking() {
  const navigate = useNavigate();

  const customerInfoStr = localStorage.getItem('customer_info');
  const customer = customerInfoStr ? JSON.parse(customerInfoStr) : null;

  // Category: 'FREIGHT_FORWARDING' or 'LOGISTICS'
  const [category, setCategory] = useState('LOGISTICS');

  // Form State
  const [serviceType, setServiceType] = useState('ROAD_REEFER');
  const [readyDate, setReadyDate] = useState('');
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [packages, setPackages] = useState('1');
  const [weight, setWeight] = useState('');
  const [volumeCbm, setVolumeCbm] = useState('');
  const [commodity, setCommodity] = useState('');
  const [remarks, setRemarks] = useState('');

  // Step 3 Fields: Cargo Packages & Dimensions (Single row in UI)
  const [packageItems, setPackageItems] = useState([
    { id: 1, packages: '', gross_weight: '', dimensions: '', unit: 'CM' }
  ]);

  const handleUpdatePackageLine = (index, field, value) => {
    setPackageItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const totalPackages = useMemo(() => {
    return packageItems.reduce((sum, item) => {
      const p = parseInt(item.packages, 10);
      return sum + (isNaN(p) ? 0 : p);
    }, 0);
  }, [packageItems]);

  const totalGrossWeight = useMemo(() => {
    return packageItems.reduce((sum, item) => {
      const w = parseFloat(item.gross_weight);
      return sum + (isNaN(w) ? 0 : w);
    }, 0);
  }, [packageItems]);

  // Step 2 Fields: Route & Load / Vehicle Specs (Domestic)
  const [loadType, setLoadType] = useState('');
  const [vehicleTypeId, setVehicleTypeId] = useState('5e3e6d6f-6e10-457e-a5a6-aa14471ff15e');
  const [vehicleTypeName, setVehicleTypeName] = useState('REEFER VEHICLE');
  const [noOfTrucks, setNoOfTrucks] = useState(1);
  const [vehicleSize, setVehicleSize] = useState('');
  const [customVehicleSize, setCustomVehicleSize] = useState('');

  // International Freight Forwarding Specific Fields
  const [intlService, setIntlService] = useState('Door To Door');
  const [cargoType, setCargoType] = useState('General');
  const [temperatureReq, setTemperatureReq] = useState('');

  const isTempCargo = useMemo(() => {
    return cargoType.includes('Temp') || cargoType === 'Perishable';
  }, [cargoType]);

  // Master options
  const [locations, setLocations] = useState([
    { id: 'loc-amd', name: 'Ahmedabad (AMD)', code: 'AMD' },
    { id: 'loc-bom', name: 'Mumbai (BOM)', code: 'BOM' },
    { id: 'loc-del', name: 'Delhi Airport (DEL)', code: 'DEL' },
    { id: 'loc-blr', name: 'Bengaluru (BLR)', code: 'BLR' },
    { id: 'loc-maa', name: 'Chennai (MAA)', code: 'MAA' },
    { id: 'loc-hyd', name: 'Hyderabad (HYD)', code: 'HYD' },
    { id: 'loc-pnq', name: 'Pune (PNQ)', code: 'PNQ' },
    { id: 'loc-mun', name: 'Mundra Port', code: 'MUNDRA' },
    { id: 'loc-jnp', name: 'Nhava Sheva (JNPT)', code: 'JNPT' }
  ]);

  const [vehicleTypes, setVehicleTypes] = useState([
    {
      id: '5e3e6d6f-6e10-457e-a5a6-aa14471ff15e',
      name: 'REEFER VEHICLE',
      sizes: ['8 FT', '12 FT', '14 FT', '16 FT', '20 FT', '22 FT', '24 FT', '32 FT']
    },
    {
      id: '0128b515-b0c7-43c1-9301-18f561f22a02',
      name: 'CONTAINOR',
      sizes: ['20 FT', '24 FT', '32 FT SXL', '32 FT MXL']
    },
    {
      id: 'bee4c8d2-8b5a-4338-814a-96f632e01b36',
      name: 'CLOSE BODY VEHICLE',
      sizes: ['14 FT', '17 FT', '19 FT', '20 FT', '22 FT', '24 FT', '32 FT']
    },
    {
      id: '2896a3f5-73d0-4c19-a128-0ef316955351',
      name: 'OPEN BODY VEHICLE',
      sizes: ['14 FT', '17 FT', '19 FT', '22 FT', '28 FT', '32 FT']
    },
    {
      id: '4d0f1314-5620-4b0e-956f-b1276b68b33e',
      name: 'OPEN TRAILER (ODC)',
      sizes: ['40 FT', '50 FT', '60 FT (ODC Heavy)']
    }
  ]);

  // Contact Details (Pre-filled from customer session if available)
  const [contactName, setContactName] = useState(customer?.contact_person || customer?.name || '');
  const [companyName, setCompanyName] = useState(customer?.business_name || customer?.company_name || '');
  const [contactEmail, setContactEmail] = useState(customer?.email || '');
  const [contactPhone, setContactPhone] = useState(customer?.mobile || customer?.phone || '');
  const [contactCity, setContactCity] = useState(customer?.city || '');

  // Submission State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submittedRequest, setSubmittedRequest] = useState(null);
  const [copiedInq, setCopiedInq] = useState(false);

  // Available vehicle sizes
  const availableSizes = useMemo(() => {
    const vt = vehicleTypes.find((v) => v.id === vehicleTypeId || v.name === vehicleTypeName);
    return vt?.sizes || [];
  }, [vehicleTypes, vehicleTypeId, vehicleTypeName]);

  const handleVehicleTypeChange = (vtId) => {
    setVehicleTypeId(vtId);
    const vt = vehicleTypes.find((v) => v.id === vtId);
    setVehicleTypeName(vt?.name || '');
    setVehicleSize('');
    setCustomVehicleSize('');
  };

  useEffect(() => {
    try {
      fetch(`${API}/api/booking-requests/public/options`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!d) return;
          if (d.locations?.length) setLocations(d.locations);
          if (d.vehicle_types?.length) setVehicleTypes(d.vehicle_types);
        })
        .catch(() => {});
    } catch {}
  }, []);

  const handleCategorySwitch = (cat) => {
    setCategory(cat);
    if (cat === 'LOGISTICS') {
      setServiceType('ROAD_REEFER');
    } else {
      setServiceType('AIR_INTERNATIONAL');
    }
  };

  const handleCopyInquiry = () => {
    const num = submittedRequest?.request_number || submittedRequest?.inquiry_number;
    if (num) {
      navigator.clipboard?.writeText(num);
      setCopiedInq(true);
      setTimeout(() => setCopiedInq(false), 2000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!fromLocation.trim() || !toLocation.trim()) {
      setError('Please provide both Origin and Destination locations.');
      return;
    }
    if (!contactName.trim() || !contactPhone.trim()) {
      setError('Please provide your Contact Name and Phone Number.');
      return;
    }

    setLoading(true);

    const activeVehicleSize = customVehicleSize.trim() || vehicleSize;
    const validItems = packageItems.filter((p) => p.packages || p.gross_weight || p.dimensions);
    const dimSummary = validItems
      .filter((p) => p.dimensions)
      .map((p) => `${p.packages ? p.packages + ' pkgs: ' : ''}${p.dimensions} ${p.unit}`)
      .join('; ');

    const structuredRemarks = category === 'FREIGHT_FORWARDING' ? [
      `[Customer Portal Booking Request - International Freight Forwarding]`,
      companyName ? `Company: ${companyName.trim()}` : null,
      contactCity ? `Location: ${contactCity.trim()}` : null,
      `Service Mode: ${serviceType}`,
      `Service Scope: ${intlService}`,
      `Cargo Type: ${cargoType}`,
      isTempCargo && temperatureReq ? `Temperature Requirement: ${temperatureReq.trim()}` : null,
      readyDate ? `Cargo Ready Date: ${readyDate}` : null,
      packages ? `Packages: ${packages}` : null,
      weight ? `Weight: ${weight} Kg` : null,
      volumeCbm ? `Volume: ${volumeCbm} CBM` : null,
      commodity ? `Commodity: ${commodity.trim()}` : null,
      remarks ? `Client Notes: ${remarks.trim()}` : null
    ].filter(Boolean).join(' | ') : [
      `[Customer Portal Booking Request - Domestic Logistics]`,
      companyName ? `Company: ${companyName.trim()}` : null,
      contactCity ? `Location: ${contactCity.trim()}` : null,
      `Service Mode: ${vehicleTypeName || serviceType}`,
      loadType ? `Load Type: ${loadType}` : null,
      activeVehicleSize ? `Vehicle Size: ${activeVehicleSize}` : null,
      noOfTrucks ? `No. of Trucks: ${noOfTrucks}` : null,
      readyDate ? `Cargo Ready Date: ${readyDate}` : null,
      remarks ? `Client Notes: ${remarks.trim()}` : null
    ].filter(Boolean).join(' | ');

    const payload = category === 'FREIGHT_FORWARDING' ? {
      category: 'FREIGHT_FORWARDING',
      service_type: serviceType,
      from_location_name: fromLocation.trim(),
      to_location_name: toLocation.trim(),
      ready_date: readyDate || null,
      vehicle_type_name: serviceType,
      vehicle_size: volumeCbm || (packages ? `${packages} Pkgs` : null),
      no_of_vehicles: 1,
      trip_type: intlService,
      load_type: cargoType,
      packages: packages || '1',
      gross_weight: weight || null,
      weight: weight || null,
      dimensions: volumeCbm || null,
      commodity: commodity || null,
      remarks: structuredRemarks,
      contact_name: contactName.trim(),
      contact_phone: contactPhone.trim(),
      contact_email: contactEmail.trim() || null,
      extra_details: {
        company_name: companyName.trim(),
        contact_city: contactCity.trim(),
        service_scope: intlService,
        cargo_type: cargoType,
        temperature_requirement: isTempCargo ? temperatureReq : null
      }
    } : {
      category: 'LOGISTICS',
      service_type: vehicleTypeName || serviceType,
      from_location_name: fromLocation.trim(),
      to_location_name: toLocation.trim(),
      ready_date: readyDate || null,
      vehicle_type_name: vehicleTypeName || serviceType,
      vehicle_size: activeVehicleSize || (packages ? `${packages} Pkgs` : null),
      no_of_vehicles: parseInt(noOfTrucks, 10) || 1,
      trip_type: null,
      load_type: loadType || 'Standard',
      packages: totalPackages > 0 ? String(totalPackages) : (packages || '1'),
      gross_weight: totalGrossWeight > 0 ? String(totalGrossWeight) : (weight || null),
      weight: totalGrossWeight > 0 ? String(totalGrossWeight) : (weight || null),
      dimensions: dimSummary || null,
      package_items: validItems.length > 0 ? validItems : null,
      remarks: structuredRemarks,
      contact_name: contactName.trim(),
      contact_phone: contactPhone.trim(),
      contact_email: contactEmail.trim() || null,
      extra_details: {
        company_name: companyName.trim(),
        contact_city: contactCity.trim(),
        vehicle_type_id: vehicleTypeId,
        vehicle_type_name: vehicleTypeName,
        no_of_trucks: noOfTrucks,
        load_type: loadType,
        vehicle_size: activeVehicleSize
      }
    };

    try {
      const token = localStorage.getItem('customer_token');
      if (!token) {
        navigate('/customer-login');
        return;
      }

      const res = await fetch(`${API}/api/client-portal/booking-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to submit booking request');
      }

      setSubmittedRequest({
        request_number: data.request_number || data.inquiry_number || `REQ-${Math.floor(1000 + Math.random() * 9000)}`,
        message: data.message || 'Your booking request has been generated and transmitted to central dispatch.'
      });
    } catch (err) {
      setError(err.message || 'Error submitting booking request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomerPortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="border-b border-slate-200 pb-4">
          <h1 className="text-2xl font-bold font-manrope text-slate-900">
            Request a Shipment Booking
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Submit your consignment parameters below. Our route specialists and control tower dispatchers will review route feasibility, allocate capacity, and issue your formal quotation.
          </p>
        </div>

        {submittedRequest ? (
          /* ─── SUCCESS CONFIRMATION STATE ─── */
          <Card className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm text-center space-y-6 max-w-3xl mx-auto animate-fadeIn">
            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-sm ring-8 ring-emerald-50/50">
              <CheckCircle2 className="w-9 h-9" />
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-600 block">
                Booking Request Successfully Transmitted
              </span>
              <div className="flex items-center justify-center gap-3">
                <h2 className="text-3xl font-display font-extrabold text-[#122b84]">
                  #{submittedRequest.request_number}
                </h2>
                <button
                  type="button"
                  onClick={handleCopyInquiry}
                  className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors cursor-pointer"
                  title="Copy Reference"
                >
                  {copiedInq ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-600 max-w-lg mx-auto leading-relaxed">
                {submittedRequest.message}
              </p>
            </div>

            {/* Shipment Summary Pill */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left text-xs space-y-3 max-w-xl mx-auto">
              <div className="font-bold text-slate-800 flex items-center justify-between border-b border-slate-200 pb-2">
                <span className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#122b84]" />
                  <span>Consignment Overview</span>
                </span>
                <span className="text-emerald-700 font-semibold bg-emerald-100/70 px-2.5 py-0.5 rounded-full text-[11px]">
                  Under Dispatcher Review
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-slate-600">
                <div>
                  <strong className="text-slate-800">Route:</strong> {fromLocation} → {toLocation}
                </div>
                <div>
                  <strong className="text-slate-800">Service:</strong>{' '}
                  {category === 'FREIGHT_FORWARDING'
                    ? `${serviceType.replace(/_/g, ' ')} (${intlService})`
                    : vehicleTypeName || serviceType.replace(/_/g, ' ')}
                </div>
                <div>
                  <strong className="text-slate-800">Contact:</strong> {contactName} {companyName ? `(${companyName})` : ''}
                </div>
                <div>
                  <strong className="text-slate-800">Phone:</strong> {contactPhone}
                </div>
                {readyDate && (
                  <div>
                    <strong className="text-slate-800">Cargo Ready:</strong> {readyDate}
                  </div>
                )}
                {category === 'FREIGHT_FORWARDING' && cargoType && (
                  <div>
                    <strong className="text-slate-800">Cargo Type:</strong> {cargoType}{' '}
                    {isTempCargo && temperatureReq ? `(${temperatureReq})` : ''}
                  </div>
                )}
                {(weight || totalGrossWeight > 0) && (
                  <div>
                    <strong className="text-slate-800">Gross Weight:</strong>{' '}
                    {category === 'FREIGHT_FORWARDING' ? weight : totalGrossWeight || weight} Kg
                  </div>
                )}
              </div>
            </div>

            {/* Notification Dispatch Notice */}
            <div className="bg-blue-50/70 border border-blue-200 rounded-xl p-4 text-left max-w-xl mx-auto text-xs space-y-2">
              <span className="font-bold text-[#122b84] flex items-center gap-2">
                <BellRing className="w-4 h-4 text-blue-600" />
                <span>Multi-Channel Team Dispatch Confirmed</span>
              </span>
              <ul className="space-y-1 text-slate-600 text-[11px] list-disc list-inside">
                <li>Booking details synchronized into central ERP booking registry</li>
                <li>WhatsApp &amp; instant desktop notification sent to on-duty route dispatcher</li>
                <li>Direct quotation &amp; rate confirmation will be issued to your portal dashboard</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Button
                onClick={() => navigate('/customer-portal/booking-requests')}
                className="w-full sm:w-auto bg-[#122b84] hover:bg-[#0a1a54] text-white font-bold rounded-xl text-xs h-11 px-6 shadow-sm"
              >
                <span>View My Booking Requests</span>
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSubmittedRequest(null);
                  setFromLocation('');
                  setToLocation('');
                  setWeight('');
                  setVolumeCbm('');
                  setCommodity('');
                  setRemarks('');
                  setTemperatureReq('');
                }}
                className="w-full sm:w-auto text-xs font-bold rounded-xl text-slate-700 border-slate-300 h-11 px-6 hover:bg-slate-50"
              >
                Submit Another Booking
              </Button>
            </div>
          </Card>
        ) : (
          /* ─── BOOKING REQUEST FORM ─── */
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-start gap-2.5 text-xs font-medium">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>{error}</div>
              </div>
            )}

            {/* 1. Step 1: Shipment Domain Switcher */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-[#122b84]">
                  Step 1: Choose Shipment Domain
                </span>
                <span className="text-xs text-slate-500">Select mode of movement</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleCategorySwitch('FREIGHT_FORWARDING')}
                  className={`p-5 rounded-2xl border-2 text-left transition-all cursor-pointer flex items-center gap-4 ${
                    category === 'FREIGHT_FORWARDING'
                      ? 'border-[#122b84] bg-white shadow-md ring-2 ring-[#122b84]/10'
                      : 'border-slate-200 bg-white/70 hover:border-slate-300'
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                      category === 'FREIGHT_FORWARDING'
                        ? 'bg-[#122b84] text-white shadow-xs'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    <Plane className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-base text-slate-900">
                      International Freight Forwarding
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Air Cargo, Ocean FCL / LCL, Customs Clearance &amp; Global Cross-Border
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleCategorySwitch('LOGISTICS')}
                  className={`p-5 rounded-2xl border-2 text-left transition-all cursor-pointer flex items-center gap-4 ${
                    category === 'LOGISTICS'
                      ? 'border-[#122b84] bg-white shadow-md ring-2 ring-[#122b84]/10'
                      : 'border-slate-200 bg-white/70 hover:border-slate-300'
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                      category === 'LOGISTICS'
                        ? 'bg-[#122b84] text-white shadow-xs'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    <Truck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-base text-slate-900">
                      Domestic Logistics &amp; Reefer Trucking
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Road FTL / LTL, Dedicated Reefer Fleet, Express Surface &amp; Rail
                    </p>
                  </div>
                </button>
              </div>
            </div>

            {/* ─── TAB-SPECIFIC FORM STEPS ─── */}
            {category === 'FREIGHT_FORWARDING' ? (
              /* ─── INTERNATIONAL FREIGHT FORWARDING: STEP 2 ─── */
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-xs space-y-6">
                <div className="border-b border-slate-100 pb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#122b84] block mb-1">
                    Step 2: Consignment &amp; Transport Specifications
                  </span>
                  <h2 className="text-lg font-display font-bold text-slate-900">
                    Routing, Mode &amp; Cargo Information
                  </h2>
                </div>

                {/* Row 1: Service Mode, Cargo Ready Date, Service (Door / Port) */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Service Mode */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Service Mode <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value)}
                      className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    >
                      <option value="AIR_INTERNATIONAL">Air Freight (International Express)</option>
                      <option value="OCEAN_FCL">Ocean Freight - FCL (Full Container Load)</option>
                      <option value="OCEAN_LCL">Ocean Freight - LCL (Consolidation)</option>
                      <option value="CUSTOMS_CLEARANCE">Customs Clearance Only (CHA Port Filing)</option>
                      <option value="MULTIMODAL_COLD_CHAIN">Multimodal Pharma Cold Chain</option>
                    </select>
                  </div>

                  {/* Cargo Ready Date */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Cargo Ready Date
                    </label>
                    <input
                      type="date"
                      value={readyDate}
                      onChange={(e) => setReadyDate(e.target.value)}
                      className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    />
                  </div>

                  {/* Service * (Door To Door, Door To Port, Port To Port, Port To Door) */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Service <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={intlService}
                      onChange={(e) => setIntlService(e.target.value)}
                      className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    >
                      <option value="Door To Door">Door To Door</option>
                      <option value="Door To Port">Door To Port</option>
                      <option value="Port To Port">Port To Port</option>
                      <option value="Port To Door">Port To Door</option>
                    </select>
                  </div>
                </div>

                {/* Row 2: Route Origin & Destination */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-blue-600" />
                      <span>Origin / Port / Airport of Loading <span className="text-red-500">*</span></span>
                    </label>
                    <input
                      type="text"
                      value={fromLocation}
                      onChange={(e) => setFromLocation(e.target.value)}
                      placeholder="City / Airport / Port of Loading (e.g. Mumbai / BOM / Nhava Sheva)"
                      required
                      className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Destination / Port / Airport of Discharge <span className="text-red-500">*</span></span>
                    </label>
                    <input
                      type="text"
                      value={toLocation}
                      onChange={(e) => setToLocation(e.target.value)}
                      placeholder="City / Airport / Port of Discharge (e.g. New York / JFK / Rotterdam / Dubai)"
                      required
                      className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    />
                  </div>
                </div>

                {/* Row 3: Cargo Type & Conditional Temperature Requirement (Text field) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-blue-600" />
                      <span>Cargo Type <span className="text-red-500">*</span></span>
                    </label>
                    <select
                      value={cargoType}
                      onChange={(e) => setCargoType(e.target.value)}
                      className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    >
                      <option value="General">General</option>
                      <option value="HAZ">HAZ</option>
                      <option value="HAZ Temp">HAZ Temp</option>
                      <option value="Non HAZ Temp">Non HAZ Temp</option>
                      <option value="Perishable">Perishable</option>
                      <option value="Valuable">Valuable</option>
                    </select>
                  </div>

                  {isTempCargo ? (
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <ThermometerSnowflake className="w-3.5 h-3.5 text-sky-600" />
                        <span>Temperature Requirement <span className="text-red-500">*</span></span>
                      </label>
                      <input
                        type="text"
                        value={temperatureReq}
                        onChange={(e) => setTemperatureReq(e.target.value)}
                        placeholder="e.g. +2°C to +8°C / -20°C / Controlled Room Temp (+15°C to +25°C)"
                        required={isTempCargo}
                        className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Commodity / Cargo Description
                      </label>
                      <input
                        type="text"
                        value={commodity}
                        onChange={(e) => setCommodity(e.target.value)}
                        placeholder="e.g. Pharmaceutical Formulations, Engineering Spares, Textiles"
                        className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                      />
                    </div>
                  )}
                </div>

                {/* Row 4: Total Packages, Weight, Volume / Container & Commodity (if Temp cargo) */}
                <div className={`grid grid-cols-1 ${isTempCargo ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-4`}>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Total Packages
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={packages}
                      onChange={(e) => setPackages(e.target.value)}
                      placeholder="e.g. 10 Boxes"
                      className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Total Weight (Kg)
                    </label>
                    <input
                      type="number"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="e.g. 750"
                      className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Approx Volume (CBM) / Container
                    </label>
                    <input
                      type="text"
                      value={volumeCbm}
                      onChange={(e) => setVolumeCbm(e.target.value)}
                      placeholder="e.g. 4.5 CBM or 1x 40ft HC"
                      className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                    />
                  </div>

                  {isTempCargo && (
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Commodity / Description
                      </label>
                      <input
                        type="text"
                        value={commodity}
                        onChange={(e) => setCommodity(e.target.value)}
                        placeholder="e.g. Vaccines, APIs, Active Biologics"
                        className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                      />
                    </div>
                  )}
                </div>

                {/* Row 5: Special Instructions */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                    Special Instructions / Handling Notes
                  </label>
                  <textarea
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Mention thermal data logger requirements, DG/Hazardous class, target arrival deadlines, or customs documentation details."
                    className="w-full p-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                  />
                </div>
              </div>
            ) : (
              /* ─── DOMESTIC LOGISTICS & REEFER TRUCKING: STEPS 2 & 3 ─── */
              <>
                {/* Domestic Step 2: Route, Load & Vehicle Specifications */}
                <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-xs space-y-6">
                  <div className="border-b border-slate-100 pb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#122b84] block mb-1">
                      Step 2: Route, Load &amp; Vehicle Specifications
                    </span>
                    <h2 className="text-lg font-display font-bold text-slate-900">
                      Consignment Route, Load Type &amp; Fleet Parameters
                    </h2>
                  </div>

                  {/* Row 1: Origin & Destination (Horizontal 2 Columns) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-blue-600" />
                        <span>Origin / Pickup Location <span className="text-red-500">*</span></span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          list="step2-locations-from"
                          placeholder="Search or enter pickup city / station..."
                          value={fromLocation}
                          onChange={(e) => setFromLocation(e.target.value)}
                          className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                          required
                        />
                        <datalist id="step2-locations-from">
                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.name} />
                          ))}
                        </datalist>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Destination / Delivery Location <span className="text-red-500">*</span></span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          list="step2-locations-to"
                          placeholder="Search or enter delivery city / destination..."
                          value={toLocation}
                          onChange={(e) => setToLocation(e.target.value)}
                          className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                          required
                        />
                        <datalist id="step2-locations-to">
                          {locations.map((loc) => (
                            <option key={loc.id} value={loc.name} />
                          ))}
                        </datalist>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Load Type, Vehicle Type, No. of Trucks (Horizontal 3 Columns) */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Load Type */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Load Type
                      </label>
                      <select
                        value={loadType}
                        onChange={(e) => setLoadType(e.target.value)}
                        className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                      >
                        <option value="">--Select--</option>
                        <option value="FTL">FTL (Full Truck Load)</option>
                        <option value="PTL">PTL (Part Truck Load)</option>
                        <option value="Dedicated Reefer">Dedicated Reefer</option>
                        <option value="Express Surface">Express Surface</option>
                      </select>
                    </div>

                    {/* Vehicle Type */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <Truck className="w-3.5 h-3.5 text-slate-500" />
                        <span>Vehicle Type</span>
                      </label>
                      <select
                        value={vehicleTypeId}
                        onChange={(e) => handleVehicleTypeChange(e.target.value)}
                        className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                      >
                        <option value="">-- Select Type --</option>
                        {vehicleTypes.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* No. of Trucks */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        No. of Trucks
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={noOfTrucks}
                        onChange={(e) => setNoOfTrucks(e.target.value)}
                        className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                      />
                    </div>
                  </div>

                  {/* Row 3: Size / Capacity & Custom Size (Horizontal 2 Columns) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Size / Capacity
                      </label>
                      <select
                        value={vehicleSize}
                        onChange={(e) => {
                          setVehicleSize(e.target.value);
                          if (e.target.value) setCustomVehicleSize('');
                        }}
                        className="w-full h-11 px-3 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                      >
                        <option value="">-- Select Size --</option>
                        {availableSizes.map((s, idx) => (
                          <option key={idx} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                        Custom Capacity / Size (Optional)
                      </label>
                      <input
                        type="text"
                        placeholder="Or type custom size (e.g. 32 FT MXL / 24 FT Reefer)..."
                        value={customVehicleSize}
                        onChange={(e) => {
                          setCustomVehicleSize(e.target.value);
                          if (e.target.value) setVehicleSize('');
                        }}
                        className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                      />
                    </div>
                  </div>
                </div>

                {/* Domestic Step 3: Cargo Packages & Dimensions */}
                <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-xs space-y-4">
                  <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-wider text-[#122b84] block mb-1">
                        Step 3: Cargo Packages &amp; Dimensions
                      </span>
                      <h2 className="text-lg font-display font-bold text-slate-900">
                        Cargo Quantities, Weights &amp; Line Dimensions
                      </h2>
                    </div>
                  </div>

                  {/* Package Line Box */}
                  <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
                    {/* Header */}
                    <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                      <Package className="w-4 h-4 text-blue-600" />
                      <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        CARGO PACKAGES &amp; DIMENSIONS
                      </h3>
                    </div>

                    {/* Single Row (Clean 12-Column Grid) */}
                    <div>
                      {packageItems.slice(0, 1).map((item, idx) => (
                        <div
                          key={item.id || idx}
                          className="grid grid-cols-12 gap-3 items-center bg-slate-50/70 p-3.5 rounded-xl border border-slate-200/80"
                        >
                          {/* No. of Packages */}
                          <div className="col-span-12 sm:col-span-3">
                            <label className="text-[11px] text-slate-600 block mb-1 font-medium">
                              No. of Packages
                            </label>
                            <input
                              type="number"
                              min="1"
                              placeholder="e.g. 10"
                              value={item.packages}
                              onChange={(e) => handleUpdatePackageLine(0, 'packages', e.target.value)}
                              className="w-full h-10 px-3 text-xs sm:text-sm bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                            />
                          </div>

                          {/* Gross Weight (Kg) */}
                          <div className="col-span-12 sm:col-span-3">
                            <label className="text-[11px] text-slate-600 block mb-1 font-medium">
                              Gross Weight (Kg)
                            </label>
                            <input
                              type="number"
                              step="any"
                              placeholder="e.g. 500"
                              value={item.gross_weight}
                              onChange={(e) => handleUpdatePackageLine(0, 'gross_weight', e.target.value)}
                              className="w-full h-10 px-3 text-xs sm:text-sm bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                            />
                          </div>

                          {/* Dimension (L × W × H) */}
                          <div className="col-span-8 sm:col-span-4">
                            <label className="text-[11px] text-slate-600 block mb-1 font-medium">
                              Dimension (L × W × H)
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. 100 x 80 x 120"
                              value={item.dimensions}
                              onChange={(e) => handleUpdatePackageLine(0, 'dimensions', e.target.value)}
                              className="w-full h-10 px-3 text-xs sm:text-sm bg-white rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                            />
                          </div>

                          {/* Unit */}
                          <div className="col-span-4 sm:col-span-2">
                            <label className="text-[11px] text-slate-600 block mb-1 font-medium">
                              Unit
                            </label>
                            <select
                              value={item.unit}
                              onChange={(e) => handleUpdatePackageLine(0, 'unit', e.target.value)}
                              className="w-full h-10 rounded-lg border border-slate-300 bg-white px-2.5 text-xs font-semibold shadow-xs focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                            >
                              <option value="CM">CM</option>
                              <option value="IN">IN</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Total Summary Footer */}
                    <div className="flex flex-col sm:flex-row items-center justify-between bg-blue-50/80 border border-blue-100 rounded-xl px-4 py-3 text-xs mt-3">
                      <span className="font-bold text-blue-900">Total Cargo Summary:</span>
                      <div className="flex gap-6 text-blue-800 font-semibold">
                        <span>
                          Total Packages: <strong className="text-blue-950 font-bold">{totalPackages}</strong>
                        </span>
                        <span>
                          Total Gross Weight:{' '}
                          <strong className="text-blue-950 font-bold">{totalGrossWeight} Kg</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ─── CONTACT DETAILS STEP (Step 3 for Int'l, Step 4 for Domestic) ─── */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-xs space-y-6">
              <div className="border-b border-slate-100 pb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-[#122b84] block mb-1">
                  Step {category === 'FREIGHT_FORWARDING' ? '3' : '4'}: Contact Person &amp; Organization Details
                </span>
                <h2 className="text-lg font-display font-bold text-slate-900">
                  Where should we send your rate quotation &amp; space confirmation?
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Contact Person Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-slate-500" />
                    <span>Your Full Name <span className="text-red-500">*</span></span>
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="e.g. Rajesh Sharma"
                    required
                    className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                  />
                </div>

                {/* Company Name */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-slate-500" />
                    <span>Company / Organization Name <span className="text-red-500">*</span></span>
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. Sun Biotech Ltd / Apex Precision"
                    required
                    className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                  />
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-slate-500" />
                    <span>Business Email Address <span className="text-red-500">*</span></span>
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="name@company.com"
                    required
                    className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                  />
                </div>

                {/* Phone / Mobile */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-slate-500" />
                    <span>Phone / WhatsApp Mobile <span className="text-red-500">*</span></span>
                  </label>
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    required
                    className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                  />
                </div>

                {/* City / Country */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-slate-500" />
                    <span>Your City &amp; Country</span>
                  </label>
                  <input
                    type="text"
                    value={contactCity}
                    onChange={(e) => setContactCity(e.target.value)}
                    placeholder="e.g. Mumbai, India / Parsippany, NJ, USA"
                    className="w-full h-11 px-3.5 text-xs sm:text-sm font-medium rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#122b84]"
                  />
                </div>
              </div>
            </div>

            {/* Submit Action Button */}
            <div className="space-y-4">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-2xl bg-[#f1b00b] hover:bg-[#d99e0a] text-[#122b84] font-display font-extrabold text-base tracking-normal transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 cursor-pointer disabled:opacity-75"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>Transmitting Booking Request to Central Dispatch...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Booking Request &amp; Get Quotation</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>

              <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-slate-500 pt-1">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" /> Priority Client Rate Quote
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-blue-600" /> Dispatch Desk Response within 60 Mins
                </span>
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-slate-400" /> Commercial Confidentiality Guaranteed
                </span>
              </div>
            </div>
          </form>
        )}
      </div>
    </CustomerPortalLayout>
  );
}
